// ─────────────────────────────────────────────────────────────────────────────
// Archivo: reportesARTService.js
// Ubicación sugerida: src/services/reportesARTService.js
//
// Reemplaza toda la lógica de useMemo del componente ReportesTramitesART:
//
//   ELIMINADO del componente:
//     - useState: tramites, estados, tiposTramite, historiales
//     - useEffect: getTramitesART + getEstados + getTipoTramites
//                + cambioEstadoService.getHistorialEstados (N llamadas)
//     - useMemo:  tramitesFiltrados, agrupacionPorEstado,
//                tiempoPromedioPorEstado, datosPie, datosBar, datosBarTiempo
//     - helpers:  resolverIdTipo, obtenerNombreEstado, normalizarTexto
//
//   LO QUE QUEDA en el componente:
//     - Estado de filtros (filtroFechaInicio, filtroFechaFin + textos)
//     - Estado de datos calculados (resumen, porEstado, tiempoPromedio)
//     - Refs de los gráficos (pieRef, barRef, barTiempoRef)
//     - Construcción de datosPie / datosBar / datosBarTiempo
//       (3 líneas cada uno, usando los arrays del backend)
//     - generarPDF (sin cambios)
//     - Render (sin cambios)
// ─────────────────────────────────────────────────────────────────────────────

import axios from 'axios';
import apiClient from '../apiClient';

const BASE_URL = '/reportes/art';

// ── Helper: construir params ──────────────────────────────────────────────────
/**
 * @param {Object} filtros
 * @param {string|null} filtros.fechaDesde  — "yyyy-MM-dd" o null
 * @param {string|null} filtros.fechaHasta  — "yyyy-MM-dd" o null
 * @returns {URLSearchParams}
 */
function buildParams(filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.fechaDesde) params.append('fechaDesde', filtros.fechaDesde);
    if (filtros.fechaHasta) params.append('fechaHasta', filtros.fechaHasta);
    return params;
}

// ── 1. Resumen general (KPIs del header) ──────────────────────────────────────
/**
 * GET /reportes/art/resumen
 *
 * Reemplaza en el componente:
 *   const total          = tramitesFiltrados.length;
 *   const estadosActivos = Object.keys(agrupacion).filter(...).length;
 *   const estadoTop      = Object.entries(agrupacion).sort(...)[0];
 *
 * @param {Object} filtros
 * @returns {Promise<{
 *   totalTramites: number,
 *   estadosActivos: number,
 *   estadoMasFrecuenteNombre: string|null,
 *   estadoMasFrecuenteCantidad: number|null
 * }>}
 *
 * Uso en el componente:
 *   const resumen = await getResumen(filtros);
 *
 *   // Cards del header:
 *   resumen.totalTramites           → card "Total Trámites ART"
 *   resumen.estadosActivos          → card "Estados Activos"
 *   resumen.estadoMasFrecuenteNombre   → card "Estado más común" (nombre)
 *   resumen.estadoMasFrecuenteCantidad → card "Estado más común" (cantidad)
 */
export async function getResumen(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/resumen`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 2. Distribución / Cantidad por estado ─────────────────────────────────────
/**
 * GET /reportes/art/por-estado
 *
 * Reemplaza en el componente:
 *   const agrupacionPorEstado = useMemo(() => {
 *       const counts = {};
 *       tramitesFiltrados.forEach(t => { ... });
 *       return counts;
 *   }, [...]);
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idEstado: number,
 *   nombreEstado: string,
 *   descripcionEstado: string|null,
 *   cantidad: number,
 *   porcentaje: number
 * }>>}
 *
 * Uso en el componente (construir datos para Chart.js):
 *
 *   const porEstado = await getPorEstado(filtros);
 *
 *   // Torta:
 *   const datosPie = {
 *       labels: porEstado.map(e => e.nombreEstado),
 *       datasets: [{
 *           data: porEstado.map(e => e.cantidad),
 *           backgroundColor: porEstado.map((_, i) => CHART_COLORS_ART[i % CHART_COLORS_ART.length]),
 *           borderColor: porEstado.map(() => '#FFF'),
 *           borderWidth: 3, hoverOffset: 12, spacing: 2,
 *       }]
 *   };
 *
 *   // Barras:
 *   const datosBar = {
 *       labels: porEstado.map(e => e.nombreEstado),
 *       datasets: [{
 *           label: 'Cantidad de trámites',
 *           data: porEstado.map(e => e.cantidad),
 *           backgroundColor: porEstado.map((_, i) => CHART_COLORS_ART[i % CHART_COLORS_ART.length]),
 *           borderColor: porEstado.map(() => 'transparent'),
 *           borderRadius: 8, barThickness: 42, maxBarThickness: 52,
 *       }]
 *   };
 */
export async function getPorEstado(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-estado`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 3. Tiempo promedio por estado ─────────────────────────────────────────────
/**
 * GET /reportes/art/tiempo-promedio
 *
 * Reemplaza en el componente el useMemo más complejo:
 *   const tiempoPromedioPorEstado = useMemo(() => {
 *       // recorre historiales[idTramite] para cada trámite
 *       // calcula fechaFin - fechaInicio por cada CambioEstado
 *       // agrupa por estado y promedia
 *       // ~40 líneas de lógica
 *   }, [tramitesFiltrados, historiales, estados]);
 *
 * La clasificación ("Rápido" / "Normal" / "Requiere atención") ya viene
 * calculada por el backend. El componente no necesita recalcularla.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idEstado: number,
 *   nombreEstado: string,
 *   diasPromedio: number,
 *   cantidadMuestras: number,
 *   clasificacion: "Rápido" | "Normal" | "Requiere atención"
 * }>>}
 *
 * Uso en el componente:
 *
 *   const tiempoPromedio = await getTiempoPromedio(filtros);
 *
 *   // Barras horizontales:
 *   const datosBarTiempo = {
 *       labels: tiempoPromedio.map(e => e.nombreEstado),
 *       datasets: [{
 *           label: 'Días promedio',
 *           data: tiempoPromedio.map(e => e.diasPromedio),
 *           backgroundColor: tiempoPromedio.map((_, i) => CHART_COLORS_ART[i % CHART_COLORS_ART.length]),
 *           borderColor: tiempoPromedio.map(() => 'transparent'),
 *           borderRadius: 8, barThickness: 36, maxBarThickness: 48,
 *       }]
 *   };
 *
 *   // Tabla detalle — los badges usan clasificacion del backend:
 *   const dias = item.diasPromedio;
 *   const bgColor = item.clasificacion === 'Requiere atención' ? '#fee2e2' : '#fef3c7';
 *   const txtColor = item.clasificacion === 'Requiere atención' ? '#b91c1c' : '#92400e';
 */
export async function getTiempoPromedio(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/tiempo-promedio`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── Helper: llamar todos los endpoints en paralelo ────────────────────────────
/**
 * Llama a los 3 endpoints en paralelo con Promise.all.
 * Recomendado para la carga inicial y al cambiar cualquier filtro.
 *
 * @param {Object} filtros
 * @returns {Promise<{
 *   resumen: Object,
 *   porEstado: Array,
 *   tiempoPromedio: Array
 * }>}
 *
 * Uso típico — reemplaza el useEffect completo del componente:
 *
 *   // ANTES (múltiples llamadas + N historiales):
 *   useEffect(() => {
 *       const cargar = async () => {
 *           setLoading(true);
 *           const [t, e, tipos] = await Promise.all([
 *               getTramitesART(0, 10000), getEstados(), getTipoTramites()
 *           ]);
 *           const tramitesData = Array.isArray(t) ? t : (t?.content || []);
 *           setTramites(tramitesData); setEstados(e); setTiposTramite(tipos);
 *           const map = {};
 *           await Promise.all(tramitesData.map(async (tr) => {
 *               map[tr.idTramite] = await cambioEstadoService.getHistorialEstados(tr.idTramite);
 *           }));
 *           setHistoriales(map);
 *           setLoading(false);
 *       };
 *       cargar();
 *   }, [filtroFechaInicio, filtroFechaFin]);
 *
 *   // DESPUÉS (3 llamadas → 1 roundtrip):
 *   useEffect(() => {
 *       const cargar = async () => {
 *           setLoading(true);
 *           try {
 *               const data = await getAllReportes({
 *                   fechaDesde: filtroFechaInicio || null,
 *                   fechaHasta: filtroFechaFin    || null,
 *               });
 *               setResumen(data.resumen);
 *               setPorEstado(data.porEstado);
 *               setTiempoPromedio(data.tiempoPromedio);
 *           } catch (err) {
 *               console.error('Error cargando reportes ART:', err);
 *           } finally {
 *               setLoading(false);
 *           }
 *       };
 *       cargar();
 *   }, [filtroFechaInicio, filtroFechaFin]);
 */
export async function getAllReportes(filtros = {}) {
    const [resumen, porEstado, tiempoPromedio] = await Promise.all([
        getResumen(filtros),
        getPorEstado(filtros),
        getTiempoPromedio(filtros),
    ]);
    return { resumen, porEstado, tiempoPromedio };
}